import { Navigate, useLocation } from "react-router-dom";
import { ReactNode } from "react";
import { jwtDecode } from "jwt-decode";
import { invalidateClientSession } from "@/shared/utils/authSession.util";
import { useAuth } from "@/hooks/useAuth";
import { RouteFallback } from "@/routes/RouteFallback";

type AdminProtectedRouteProps = {
  children: ReactNode;
};

type DecodedToken = {
  id: string;
  email: string;
  role: "ADMIN" | "CLIENT" | "FACILITATOR" | "SUB_ADMIN" | "ECOSYSTEM_HEAD" | "EXPERT" | "MANAGER";
  exp: number;
  iat: number;
};

export function AdminProtectedRoute({ children }: AdminProtectedRouteProps) {
  const location = useLocation();
  const { token, isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <RouteFallback />;
  }

  if (!isAuthenticated || !token) {
    return <Navigate to="/admin/login" state={{ from: location }} replace />;
  }

  try {
    const decoded = jwtDecode<DecodedToken>(token);

    // Do not wipe session on JWT exp here — useAuth + silent refresh renew access.
    // Role checks only for correct portal.

    if (decoded.role === "CLIENT" || decoded.role === "MANAGER") {
      return <Navigate to="/profile" replace />;
    }

    if (decoded.role === "FACILITATOR") {
      return <Navigate to="/facilitator/dashboard" replace />;
    }

    if (decoded.role === "ECOSYSTEM_HEAD") {
      return <Navigate to="/ecosystem-head/dashboard" replace />;
    }

    if (decoded.role === "EXPERT") {
      return <Navigate to="/expert/dashboard" replace />;
    }

    if (decoded.role !== "ADMIN" && decoded.role !== "SUB_ADMIN") {
      invalidateClientSession();
      return <Navigate to="/admin/login" state={{ from: location }} replace />;
    }

    return <>{children}</>;
  } catch {
    invalidateClientSession();
    return <Navigate to="/admin/login" state={{ from: location }} replace />;
  }
}
